import {
  AcoFormData,
  AdgroupFormData,
  AdType,
  AppInfo,
  CampaignFormData,
  FullNormalFormData,
  RejectInfo,
  CreateMode,
} from '@tt4b/ttam-enums';
import { useCreativeAdgroupIs, useStdAdgroupIs, useStdCampaignIs, useAllowlist } from '@tt4b/ttam-hooks';
import { i18n } from '@tt4b/ttam-i18n';
import { Ref, computed } from '@vue/composition-api';
import { useTrackUrl, useTrackUrlShow } from './hooks/useTrackUrl';
import { useViewAbility } from './hooks/useViewAbility';
import { useBrandSafetyProps } from './hooks/useBrandSafetyProps';

export function usePropsImpressionUrl(props: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  currentAdType: Ref<AdType>;
}) {
  const { adgroupData, campaignData, currentAdType } = props;
  const { isLiveAds } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const isOnlyShowImpressionUrl = computed(() => isLiveAds.value && currentAdType.value === AdType.SINGLE_VIDEO);
  const trackingUrlTitle = computed(() =>
    isOnlyShowImpressionUrl.value ? i18n.t('module_adgroup_only_impression_url') : i18n.t('adgroup_track_url'),
  );
  return {
    isOnlyShowImpressionUrl,
    trackingUrlTitle,
  };
}

/**
 * 针对闭环下线的tracking - Third party tracking settings判断，保证回显正常
 */
export function usePropsTrackCloseLoopVisible(opt: {
  campaignData: Ref<CampaignFormData>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
  createMode: Ref<string>;
}) {
  const { campaignData, creativeData, createMode } = opt;

  const { isProductSalesCloseLoop } = useStdCampaignIs({ campaignFormData: campaignData });
  const { isCloseLoopTrackUrlWhite } = useAllowlist();

  const closeLoopTrackingShow = computed(() => {
    if (!isProductSalesCloseLoop.value) {
      return true;
    }
    const { trackingPixelId, trackingAppId, trackUrl, actionTrackUrl } = creativeData.value;
    return Boolean(
      (createMode.value === CreateMode.EDIT &&
        (trackingPixelId || (trackingAppId && trackingAppId !== '0') || trackUrl?.[0] || actionTrackUrl?.[0])) ||
        isCloseLoopTrackUrlWhite?.value,
    );
  });
  return {
    closeLoopTrackingShow,
  };
}

export function useIsShowTrackingMore(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
  appInfo: Ref<AppInfo>;
  rejectInfo: Ref<RejectInfo>;
  isDisabledCreative: Ref<boolean>;
  createMode: Ref<string>;
}) {
  const { campaignData, adgroupData, creativeData, appInfo, rejectInfo, isDisabledCreative, createMode } = opt;
  const { isShowTrackingUrl } = useTrackUrlShow({
    campaignData,
    adgroupData,
    creativeData,
    appInfo,
    rejectInfo,
  });
  const { isShowViewability } = useViewAbility({
    adgroupData,
    creativeData,
    campaignData,
    createMode,
  });

  const { isShowBrandSafety } = useBrandSafetyProps({
    adgroupData,
    campaignData,
    creativeData,
    isDisabledCreative,
  });

  const { closeLoopTrackingShow } = usePropsTrackCloseLoopVisible({
    campaignData,
    creativeData,
    createMode,
  });

  const isShowTrackingMore = computed(
    () => (isShowTrackingUrl && closeLoopTrackingShow.value) || isShowViewability.value || isShowBrandSafety.value,
  );

  return {
    isShowTrackingMore,
  };
}

export function usePropsTrackUrlAllHide(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  creativeData: Ref<FullNormalFormData & AcoFormData>;
  appInfo: Ref<AppInfo>;
  createMode: Ref<string>;
  isDisabledCreative: Ref<boolean>;
  rejectInfo: Ref<RejectInfo>;
}) {
  const { campaignData, adgroupData, creativeData, appInfo, createMode, rejectInfo, isDisabledCreative } = opt;

  const { isShowTrackingMore } = useIsShowTrackingMore({
    campaignData,
    adgroupData,
    creativeData,
    appInfo,
    rejectInfo,
    isDisabledCreative,
    createMode,
  });
  const { isShowPixel } = useTrackUrl({
    campaignData,
    adgroupData,
    creativeData,
    appInfo,
    createMode,
    isDisabledCreative,
  });

  const isTrackUrlAllHide = computed(() => !isShowPixel.value && !isShowTrackingMore.value);

  return {
    isTrackUrlAllHide,
  };
}

export function usePropsTrackTipVisible(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  createMode: Ref<string>;
}) {
  const { campaignData, adgroupData, createMode } = opt;
  const { isRetargetingApp } = useCreativeAdgroupIs({
    campaignFormData: campaignData,
    adgroupFormData: adgroupData,
    createMode,
  });

  const visible = computed(() => isRetargetingApp.value);

  return {
    visible,
  };
}
